<template>
  <div class="decisions-page" data-testid="user-projects-decisions-view">
    <div class="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div>
        <h1 class="text-[24px] font-bold text-[var(--color-dark)] m-0 leading-tight">
          {{ $t('UserProjects.decisions.pageTitle') }}
        </h1>
        <p class="text-sm text-[var(--color-disabled)] mt-1 mb-0">
          {{ $t('UserProjects.decisions.subtitle') }}
        </p>
      </div>
      <button
        type="button"
        class="decisions-page__create-btn"
        data-testid="decisions-create"
        :disabled="!createProjectId"
        @click="openCreateDialog"
      >
        <i class="pi pi-plus" aria-hidden="true" />
        {{ $t('UserProjects.decisions.create') }}
      </button>
    </div>

    <Message v-if="!createProjectId && userProjectContextReady" severity="info" :closable="false">
      {{ $t('UserProjects.context.required') }}
    </Message>

    <Message v-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div
      v-if="userProjectContextReady && !loading"
      class="flex flex-wrap items-center gap-3 mb-4"
      data-testid="decisions-filter-bar"
    >
      <div class="flex items-center gap-1.5 flex-wrap" data-testid="decisions-status-filters">
        <button
          v-for="option in statusFilterOptions"
          :key="option.value"
          type="button"
          class="decisions-page__pill"
          :class="{ 'decisions-page__pill--active': statusFilter === option.value }"
          :data-testid="`decisions-status-${option.value}`"
          @click="setStatusFilter(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="flex items-center gap-2 flex-wrap">
        <Select
          v-model="deciderFilterProxy"
          :options="deciderOptions"
          option-label="label"
          option-value="value"
          show-clear
          class="decisions-page__select"
          :placeholder="$t('UserProjects.decisions.filters.decider')"
          data-testid="decisions-filter-decider"
        />
        <Select
          v-model="projectFilterProxy"
          :options="projectOptions"
          option-label="label"
          option-value="value"
          show-clear
          class="decisions-page__select"
          :placeholder="$t('UserProjects.decisions.filters.project')"
          data-testid="decisions-filter-project"
        />
        <Select
          v-model="milestoneFilterProxy"
          :options="milestoneOptions"
          option-label="label"
          option-value="value"
          show-clear
          class="decisions-page__select"
          :placeholder="$t('UserProjects.decisions.filters.milestone')"
          data-testid="decisions-filter-milestone"
        />
        <Select
          v-model="dueFilterProxy"
          :options="dueOptions"
          option-label="label"
          option-value="value"
          show-clear
          class="decisions-page__select"
          :placeholder="$t('UserProjects.decisions.filters.due')"
          data-testid="decisions-filter-due"
        />
        <Select
          v-model="escalationFilterProxy"
          :options="escalationOptions"
          option-label="label"
          option-value="value"
          show-clear
          class="decisions-page__select"
          :placeholder="$t('UserProjects.decisions.filters.escalation')"
          data-testid="decisions-filter-escalation"
        />
        <Button
          icon="pi pi-refresh"
          severity="secondary"
          :loading="loading"
          data-testid="decisions-refresh"
          @click="refresh"
        />
      </div>
    </div>

    <div v-if="loading || !userProjectContextReady" class="flex items-center justify-center py-16 text-[var(--color-disabled)]">
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('UserProjects.decisions.loading') }}
    </div>

    <template v-else>
      <DecisionKpiStrip :decisions="filteredDecisions" />

      <OverviewDecisionAnalyticsPanel
        v-if="createProjectId"
        :data="analyticsData"
        :loading="analyticsLoading"
        :error="analyticsError"
      />

      <div class="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div class="lg:col-span-2 space-y-2">
          <p class="text-[12px] text-[var(--color-disabled)] m-0">
            {{ $t('UserProjects.decisions.count', { count: filteredDecisions.length }) }}
          </p>

          <div
            v-if="isEmpty"
            class="bg-white rounded-[10px] p-6 border border-[#E5E5E5] text-center"
            data-testid="decisions-empty"
          >
            <p class="text-[13px] text-[var(--color-disabled)] m-0">
              {{ $t('UserProjects.decisions.emptyFiltered') }}
            </p>
          </div>

          <DecisionListCard
            v-for="item in filteredDecisions"
            :key="item.id"
            :decision="item"
            :selected="selectedDecision?.id === item.id"
            :decider-label="userLabel(item.decisionOwnerDisplayName, item.decisionOwnerUserId)"
            @select="selectDecision"
          />
        </div>

        <div class="lg:col-span-3">
          <DecisionDetailPanel
            :decision="selectedDecision"
            :decider-label="userLabel(selectedDecision?.decisionOwnerDisplayName, selectedDecision?.decisionOwnerUserId)"
            :preparer-label="userLabel(selectedDecision?.preparedByDisplayName, selectedDecision?.preparedByUserId)"
            @edit="openEditDialog"
            @mark-status="handleMarkStatus"
            @escalate="handleEscalate"
            @delete="confirmDelete"
          />
        </div>
      </div>
    </template>

    <DecisionFormDialog
      v-model:visible="formDialogVisible"
      :form-mode="formMode"
      :initial-decision="editingDecision"
      :project-name="createProjectName"
      :participant-users="participantUsers"
      :milestone-options="milestoneOptionsForForm"
      :default-owner-user-id="currentUserId"
      :saving="saving"
      @submit="submitForm"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Select from 'primevue/select';
import DecisionKpiStrip from '@/components/CustomerApp/UserProjects/DecisionKpiStrip.vue';
import OverviewDecisionAnalyticsPanel from '@/components/CustomerApp/UserProjects/OverviewDecisionAnalyticsPanel.vue';
import DecisionListCard from '@/components/CustomerApp/UserProjects/DecisionListCard.vue';
import DecisionDetailPanel from '@/components/CustomerApp/UserProjects/DecisionDetailPanel.vue';
import DecisionFormDialog from '@/components/CustomerApp/UserProjects/DecisionFormDialog.vue';
import pmDecisionAnalyticsService from '@/services/pmDecisionAnalyticsService';
import {
  buildDeciderFilterOptions,
  buildMilestoneFilterOptions,
  buildProjectFilterOptions,
  DECISION_STATUS_FILTER_VALUES,
  normalizeDecisionRow,
} from '@/utils/decisionDisplay';
import { resolveCurrentUserId } from '@/utils/resolveCurrentUserId';

export default {
  name: 'UserProjectsDecisionsView',
  components: {
    Button,
    Message,
    Select,
    DecisionKpiStrip,
    OverviewDecisionAnalyticsPanel,
    DecisionListCard,
    DecisionDetailPanel,
    DecisionFormDialog,
  },
  inject: {
    participantUsersProvider: { from: 'participantUsers', default: () => () => [] },
  },
  setup() {
    return {
      confirm: useConfirm(),
      toast: useToast(),
    };
  },
  data() {
    return {
      formDialogVisible: false,
      formMode: 'create',
      editingDecision: null,
      saving: false,
      analyticsData: null,
      analyticsLoading: false,
      analyticsError: null,
    };
  },
  computed: {
    ...mapGetters('pmDecisionsPage', [
      'items',
      'loading',
      'error',
      'statusFilter',
      'deciderFilter',
      'projectFilter',
      'milestoneFilter',
      'dueFilter',
      'escalationFilter',
      'filteredDecisions',
      'selectedDecision',
      'isEmpty',
    ]),
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
      selectedProject: 'selectedProject',
      activeProjects: 'activeProjects',
      userProjectContextReady: 'userProjectContextReady',
    }),
    ...mapGetters('pmMilestone', {
      milestoneOptionsForForm: 'milestoneOptions',
    }),
    ...mapGetters(['getUser']),
    participantUsers() {
      return this.participantUsersProvider() || [];
    },
    currentUserId() {
      return resolveCurrentUserId(this.getUser);
    },
    createProjectId() {
      return this.selectedProjectId;
    },
    createProjectName() {
      if (this.formMode === 'edit' && this.editingDecision?.projectName) {
        return this.editingDecision.projectName;
      }
      return this.selectedProject?.name || this.selectedProject?.title || '';
    },
    statusFilterOptions() {
      return DECISION_STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: this.$t(`UserProjects.decisions.statusFilters.${value}`),
      }));
    },
    deciderOptions() {
      return buildDeciderFilterOptions(this.items, this.participantUsers);
    },
    projectOptions() {
      const fromProjects = (this.activeProjects || []).map((project) => ({
        value: String(project.id),
        label: project.name || project.title || String(project.id),
      }));
      if (fromProjects.length > 0) {
        return fromProjects.sort((a, b) => a.label.localeCompare(b.label));
      }
      return buildProjectFilterOptions(this.items);
    },
    milestoneOptions() {
      return buildMilestoneFilterOptions(this.items);
    },
    dueOptions() {
      return [
        { value: 'overdue', label: this.$t('UserProjects.decisions.dueFilters.overdue') },
        { value: 'thisWeek', label: this.$t('UserProjects.decisions.dueFilters.thisWeek') },
        { value: 'next30Days', label: this.$t('UserProjects.decisions.dueFilters.next30Days') },
      ];
    },
    escalationOptions() {
      return [
        { value: 'high', label: this.$t('UserProjects.decisions.escalation.high') },
        { value: 'medium', label: this.$t('UserProjects.decisions.escalation.medium') },
        { value: 'none', label: this.$t('UserProjects.decisions.escalation.none') },
      ];
    },
    deciderFilterProxy: {
      get() { return this.deciderFilter || null; },
      set(value) { this.setDeciderFilter(value || ''); },
    },
    projectFilterProxy: {
      get() { return this.projectFilter || null; },
      set(value) { this.onProjectFilterChange(value); },
    },
    milestoneFilterProxy: {
      get() { return this.milestoneFilter || null; },
      set(value) { this.setMilestoneFilter(value || ''); },
    },
    dueFilterProxy: {
      get() { return this.dueFilter || null; },
      set(value) { this.setDueFilter(value || ''); },
    },
    escalationFilterProxy: {
      get() { return this.escalationFilter || null; },
      set(value) { this.setEscalationFilter(value || ''); },
    },
  },
  watch: {
    selectedProjectId: {
      immediate: true,
      handler(projectId, previousId) {
        if (projectId) {
          this.loadMilestonesForForm(projectId);
          this.syncProjectFilterFromContext(projectId);
        }
        if (previousId !== undefined && projectId !== previousId) {
          this.setMilestoneFilter('');
        }
        if (this.userProjectContextReady && previousId !== undefined) {
          this.refresh();
        }
      },
    },
    userProjectContextReady: {
      immediate: true,
      handler(isReady) {
        if (!isReady) return;
        this.syncProjectFilterFromContext(this.selectedProjectId);
        this.refresh();
        this.maybeOpenCreateFromQuery();
      },
    },
    '$route.query.create'() {
      this.maybeOpenCreateFromQuery();
    },
  },
  methods: {
    ...mapActions('pmDecisionsPage', [
      'fetchPortfolioDecisions',
      'setStatusFilter',
      'setDeciderFilter',
      'setProjectFilter',
      'setMilestoneFilter',
      'setDueFilter',
      'setEscalationFilter',
      'selectDecision',
      'patchDecisionItem',
      'removeDecisionItem',
    ]),
    ...mapActions('pmDecision', [
      'setProjectContext',
      'createDecision',
      'updateDecision',
      'updateDecisionStatus',
      'escalateDecision',
      'deleteDecision',
    ]),
    ...mapActions('pmMilestone', {
      setMilestoneProjectContext: 'setProjectContext',
    }),
    ...mapActions('pmProject', ['changeUserProjectContext']),
    ...mapActions('pmCommitment', ['fetchUserProjectsOverview']),

    syncProjectFilterFromContext(projectId) {
      if (!projectId) return;
      const normalized = String(projectId);
      if (String(this.projectFilter) !== normalized) {
        this.setProjectFilter(normalized);
      }
    },

    async onProjectFilterChange(value) {
      const next = value ? String(value) : '';
      await this.setProjectFilter(next);
      if (next && next !== String(this.selectedProjectId)) {
        try {
          await this.changeUserProjectContext(next);
        } catch {
          // handled in pmProject store
        }
      }
    },

    async loadMilestonesForForm(projectId) {
      try {
        await this.setMilestoneProjectContext(projectId);
      } catch {
        // milestone form options optional
      }
    },

    userLabel(displayName, userId) {
      if (displayName) return displayName;
      if (!userId) return '—';
      const user = this.participantUsers.find((entry) => String(entry.id) === String(userId));
      return user?.name || user?.email || userId;
    },

    async refresh() {
      if (!this.userProjectContextReady) return;
      try {
        await this.fetchPortfolioDecisions();
        await this.fetchDecisionAnalytics();
      } catch {
        // error in store
      }
    },

    async fetchDecisionAnalytics() {
      if (!this.createProjectId) {
        this.analyticsData = null;
        this.analyticsError = null;
        return null;
      }
      this.analyticsLoading = true;
      this.analyticsError = null;
      try {
        this.analyticsData = await pmDecisionAnalyticsService.getProjectDecisionAnalytics(
          this.createProjectId,
          { weeks: 8 },
        );
        return this.analyticsData;
      } catch (error) {
        this.analyticsError = error?.message || this.$t('UserProjects.decisions.analytics.loadError');
        this.analyticsData = null;
        return null;
      } finally {
        this.analyticsLoading = false;
      }
    },

    maybeOpenCreateFromQuery() {
      if (this.$route.query.create === '1' && this.createProjectId) {
        this.openCreateDialog();
        const query = { ...this.$route.query };
        delete query.create;
        this.$router.replace({ query });
      }
    },

    openCreateDialog() {
      if (!this.createProjectId) return;
      this.formMode = 'create';
      this.editingDecision = null;
      this.formDialogVisible = true;
    },

    openEditDialog(decision) {
      this.formMode = 'edit';
      this.editingDecision = decision;
      this.formDialogVisible = true;
    },

    async withProjectContext(projectId, action) {
      await this.setProjectContext(projectId);
      return action();
    },

    async submitForm(payload) {
      const projectId = this.formMode === 'create'
        ? this.createProjectId
        : this.editingDecision?.projectId;
      if (!projectId) return;

      this.saving = true;
      try {
        let saved;
        if (this.formMode === 'create') {
          saved = await this.withProjectContext(projectId, () => this.createDecision(payload));
          this.toast.add({
            severity: 'success',
            summary: this.$t('ProjectManagement.decisions.toast.created'),
            life: 3000,
          });
        } else {
          saved = await this.withProjectContext(projectId, () => this.updateDecision({
            id: this.editingDecision.id,
            payload,
          }));
          this.toast.add({
            severity: 'success',
            summary: this.$t('ProjectManagement.decisions.toast.updated'),
            life: 3000,
          });
        }
        this.formDialogVisible = false;
        const project = this.projectOptions.find((entry) => entry.value === String(projectId));
        this.patchDecisionItem(normalizeDecisionRow(saved, {
          projectId,
          projectName: project?.label || this.editingDecision?.projectName || '',
        }));
        await this.fetchUserProjectsOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.decisions.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },

    async handleMarkStatus({ decision, status }) {
      try {
        const updated = await this.withProjectContext(decision.projectId, () => this.updateDecisionStatus({
          id: decision.id,
          status,
        }));
        this.patchDecisionItem(normalizeDecisionRow(updated, {
          projectId: decision.projectId,
          projectName: decision.projectName,
        }));
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.decisions.toast.statusUpdated'),
          life: 3000,
        });
        await this.fetchUserProjectsOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.decisions.toast.error'),
          life: 4000,
        });
      }
    },

    async handleEscalate(decision) {
      try {
        const updated = await this.withProjectContext(decision.projectId, () => this.escalateDecision(decision.id));
        this.patchDecisionItem(normalizeDecisionRow(updated, {
          projectId: decision.projectId,
          projectName: decision.projectName,
        }));
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.decisions.toast.escalated'),
          life: 3000,
        });
        await this.fetchDecisionAnalytics();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.decisions.toast.error'),
          life: 4000,
        });
      }
    },

    confirmDelete(decision) {
      this.confirm.require({
        message: this.$t('ProjectManagement.decisions.deleteConfirm', { name: decision.title }),
        header: this.$t('ProjectManagement.decisions.deleteTitle'),
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.withProjectContext(decision.projectId, () => this.deleteDecision(decision.id));
            this.removeDecisionItem(decision.id);
            this.toast.add({
              severity: 'success',
              summary: this.$t('ProjectManagement.decisions.toast.deleted'),
              life: 3000,
            });
            await this.fetchUserProjectsOverview();
          } catch {
            this.toast.add({
              severity: 'error',
              summary: this.$t('ProjectManagement.decisions.toast.error'),
              life: 4000,
            });
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.decisions-page__pill {
  padding: 0.375rem 0.875rem;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  background: #F5F5F5;
  color: #6B6B6B;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.decisions-page__pill:hover:not(.decisions-page__pill--active) {
  background: #EEEEEE;
}

.decisions-page__pill--active {
  background: var(--accent-gradient);
  color: var(--color-white);
}

.decisions-page__create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 10px;
  background: var(--accent-gradient);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.decisions-page__create-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.decisions-page__select {
  min-width: 9rem;
}
</style>

<style>
.decisions-page .decisions-page__select.p-select {
  min-height: 2.25rem;
  border-radius: 10px;
  border-color: #e5e5e5;
  font-size: 13px;
  font-weight: 500;
}

.decisions-page .decisions-page__select .p-select-label {
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  font-size: 13px;
  font-weight: 500;
}

.decisions-page .decisions-page__select:not(.p-disabled):hover {
  border-color: #d4d4d4;
}

.decisions-page .decisions-page__select:not(.p-disabled).p-focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 2px rgba(228, 130, 26, 0.15);
}
</style>
